import { useState, useEffect } from "react";
import axios from "axios";
import logo from "../../public/logo.jpg";
import bgVideo from "../../public/video.mp4";

interface CartItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  image_url: string;
  caliber: string;
}

export default function App() {
  const [activeTab, setActiveTab] = useState("inicio");
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<number | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    full_name: "",
    dpi: "",
    phone: "",
    email: "",
    type_license: "primera_licencia",
  });

  const API_URL = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" 
    ? "http://localhost/2026/ak47_armeria/api/index.php" 
    : "https://armeriak47.com/api/index.php"; 
  
  const LOGO_URL = logo;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    fetchProducts();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Bloquear scroll cuando el menú móvil o la modal están abiertos
    if (mobileMenuOpen || selectedProduct) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [mobileMenuOpen, selectedProduct]);

  const fetchProducts = async () => {
    try {
      // 1. Obtencion de productos
      const response = await axios.get(API_URL);
      const data = response.data;
      setProducts(data);

      // 2. Obtencion de arbol de categorias
      const catResponse = await axios.get(`${API_URL}?get_categories=1`);
      const catTree = catResponse.data;
      
      // Ordenamos alfabeticamente o por ids
      catTree.sort((a: any,b: any) => a.id - b.id);
      
      setCategories(catTree);
    } catch (error) {
      console.error("Error cargando arsenal:", error);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(API_URL, formData);
      alert("¡Trámite enviado! Nos contactaremos por WhatsApp.");
      setFormData({
        full_name: "",
        dpi: "",
        phone: "",
        email: "",
        type_license: "primera_licencia",
      });
      setActiveTab("inicio");
    } catch (error) {
      alert("Error en el servidor");
    }
  };

  const addToCart = (product: any) => {
    setCart((prevCart) => {
      const existing = prevCart.find((item) => item.id === product.id);
      if (existing) {
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }
      return [
        ...prevCart,
        {
          id: product.id,
          name: product.name,
          price: parseFloat(product.price),
          quantity: 1,
          image_url: product.image_url,
          caliber: product.caliber,
        },
      ];
    });
    setCartOpen(true);
  };

  const removeFromCart = (id: number) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== id));
  };

  const getTotalPrice = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const sendWhatsAppMessage = () => {
    if (cart.length === 0) return;
    let message = `¡Hola Armería 47! Estoy interesado en estos productos:\n\n`;
    cart.forEach((item, index) => {
      message += `${index + 1}. ${item.name} (${item.caliber})\n   Cant: ${item.quantity} - Sub: Q${(item.price * item.quantity).toLocaleString()}\n\n`;
    });
    message += `💰 TOTAL ESTIMADO: Q${getTotalPrice().toLocaleString()}`;
    window.open(
      `https://wa.me/50230319972?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  const handleNavClick = (tab: string) => {
    setActiveTab(tab);
    setSelectedSubCategory('all');
    setSearchQuery('');
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderProductCard = (product: any) => (
    <div
      key={product.id}
      className="group bg-zinc-900/60 backdrop-blur-md rounded-3xl overflow-hidden border border-zinc-800 hover:border-amber-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-500/10 flex flex-col"
    >
      <div
        className="relative h-48 md:h-56 overflow-hidden bg-black/50 cursor-pointer"
        onClick={() => setSelectedProduct(product)}
      >
        <img
          src={
            product.image_url ||
            "https://via.placeholder.com/400x300?text=Sin+Imagen"
          }
          className="w-full h-full object-cover opacity-80 group-hover:scale-110 transition-transform duration-700"
          alt={product.name}
        />
        <div className="absolute top-3 right-3 px-3 py-1 bg-amber-500 text-black text-[10px] font-black rounded-full uppercase italic shadow-lg z-10">
          {product.category_name || "Táctico"}
        </div>
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="bg-black/80 backdrop-blur text-white px-4 py-2 rounded-full border border-amber-500/50 flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
            <i className="ri-eye-line text-amber-500"></i>
            <span className="text-xs font-bold uppercase tracking-widest">
              Ver Detalle
            </span>
          </div>
        </div>
      </div>

      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <p className="text-[10px] font-bold text-amber-500 tracking-widest uppercase mb-1">
          {product.brand}
        </p>
        <h3
          className="text-xs md:text-sm font-black italic uppercase text-white mb-2 leading-tight cursor-pointer hover:text-amber-500 transition-colors"
          onClick={() => setSelectedProduct(product)}
        >
          {product.name}
        </h3>
        <p className="text-zinc-400 text-xs font-bold tracking-wider mb-4 border-b border-zinc-800 pb-2">
          CALIBRE: {product.caliber}
        </p>

        <div className="flex items-center justify-between mt-auto">
          <span className="text-xl font-black italic text-white">
            Q{Number(product.price).toLocaleString()}
          </span>
          <button
            onClick={() => addToCart(product)}
            className="bg-gradient-to-r from-amber-500 to-amber-600 p-3 rounded-xl text-black hover:scale-105 transition-all shadow-lg active:scale-95 group/btn relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-500"></div>
            <i className="ri-shopping-basket-2-line text-lg font-bold"></i>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen text-white selection:bg-amber-500 selection:text-black font-sans overflow-x-hidden pb-24 md:pb-0">
      {/* VIDEO DE FONDO */}
      <div className="fixed inset-0 z-[-1] w-full h-full overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src={bgVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"></div>
      </div>

      {/* === NAVBAR === */}
      <nav
        className={`fixed top-0 w-full z-[60] transition-all duration-300 ${isScrolled || mobileMenuOpen ? "bg-black/80 backdrop-blur-xl border-b border-amber-500/10 py-2 shadow-2xl" : "bg-gradient-to-b from-black/90 to-transparent py-4"}`}
      >
        <div className="w-full px-4 md:px-10 h-16 md:h-20 flex items-center">
          {/* LOGO (Lado Izquierdo) */}
          <div className="flex-1 flex justify-center md:justify-start items-center">
            <div
              className="cursor-pointer active:scale-95 transition-transform"
              onClick={() => handleNavClick("inicio")}
            >
              <img
                src={LOGO_URL}
                className="w-52 h-auto md:w-auto md:h-28 object-contain drop-shadow-xl"
                alt="Armería AK-47"
              />
            </div>
          </div>

          {/* MENÚ (Centro) Solo Principales */}
          <div className="hidden lg:flex justify-center flex-initial">
            <div className="flex gap-1 bg-zinc-900/80 p-1.5 rounded-2xl border border-white/5 backdrop-blur-md shadow-2xl items-center">
              
              <button onClick={() => handleNavClick('inicio')} className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all duration-300 ${activeTab === 'inicio' ? 'text-black bg-amber-500 shadow-lg shadow-amber-500/20' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}>INICIO</button>
              
              {categories.map((cat: any) => (
                <button key={cat.id} onClick={() => handleNavClick('cat_' + cat.id)} className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-1 ${activeTab === 'cat_' + cat.id ? 'text-black bg-amber-500 shadow-lg shadow-amber-500/20' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}>
                  {cat.name}
                </button>
              ))}

              <button onClick={() => handleNavClick('tramites')} className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all duration-300 ${activeTab === 'tramites' ? 'text-black bg-amber-500 shadow-lg shadow-amber-500/20' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}>TRÁMITES</button>
              <button onClick={() => handleNavClick('nosotros')} className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all duration-300 ${activeTab === 'nosotros' ? 'text-black bg-amber-500 shadow-lg shadow-amber-500/20' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}>NOSOTROS</button>
              <button onClick={() => handleNavClick('contacto')} className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all duration-300 ${activeTab === 'contacto' ? 'text-black bg-amber-500 shadow-lg shadow-amber-500/20' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}>CONTACTO</button>
            </div>
          </div>

          {/* CARRITO Y MENÚ MÓVIL (Lado Derecho) */}
          <div className="flex-1 flex justify-end items-center gap-3">
            <button
              onClick={() => setCartOpen(true)}
              className="relative bg-zinc-900/80 border border-zinc-800 text-amber-500 w-10 h-10 md:w-auto md:h-auto md:px-5 md:py-2.5 rounded-xl font-black text-xs hover:border-amber-500/50 transition-all flex items-center justify-center gap-2 group"
            >
              <i className="ri-shopping-cart-2-fill text-lg group-hover:scale-110 transition-transform"></i>
              <span className="hidden md:inline">CARRITO</span>
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center border border-black animate-pulse">
                  {cart.length}
                </span>
              )}
            </button>

            <button
              className="lg:hidden text-2xl text-amber-500 w-10 h-10 flex items-center justify-center active:scale-90 transition-transform"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i
                className={mobileMenuOpen ? "ri-close-line" : "ri-menu-4-line"}
              ></i>
            </button>
          </div>
        </div>
      </nav>

      {/* === MENÚ MÓVIL === */}
      <div className={`fixed inset-0 z-50 flex flex-col pt-24 bg-black/95 backdrop-blur-xl transition-all duration-500 lg:hidden ${mobileMenuOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-full'}`}>
        <div className="flex flex-col items-center w-full px-6 space-y-1 overflow-y-auto max-h-[80vh] custom-scrollbar">
          
          <button onClick={() => handleNavClick('inicio')} className={`w-full py-4 text-center text-base font-bold uppercase tracking-[0.2em] border-b border-white/5 transition-all duration-300 ${activeTab === 'inicio' ? 'text-amber-500 bg-amber-500/5' : 'text-zinc-300 hover:text-white'}`}>INICIO</button>
          
          {categories.map((cat: any) => (
            <button key={cat.id} onClick={() => handleNavClick('cat_' + cat.id)} className={`w-full py-4 text-center text-base font-bold uppercase tracking-[0.2em] transition-all duration-300 border-b border-white/5 ${activeTab === 'cat_' + cat.id ? 'text-amber-500 bg-amber-500/5' : 'text-zinc-300 hover:text-white'}`}>
              {cat.name}
            </button>
          ))}

          <button onClick={() => handleNavClick('tramites')} className={`w-full py-4 text-center text-base font-bold uppercase tracking-[0.2em] border-b border-white/5 transition-all duration-300 ${activeTab === 'tramites' ? 'text-amber-500 bg-amber-500/5' : 'text-zinc-300 hover:text-white'}`}>TRÁMITES</button>
          <button onClick={() => handleNavClick('nosotros')} className={`w-full py-4 text-center text-base font-bold uppercase tracking-[0.2em] border-b border-white/5 transition-all duration-300 ${activeTab === 'nosotros' ? 'text-amber-500 bg-amber-500/5' : 'text-zinc-300 hover:text-white'}`}>NOSOTROS</button>
          <button onClick={() => handleNavClick('contacto')} className={`w-full py-4 text-center text-base font-bold uppercase tracking-[0.2em] border-b border-white/5 transition-all duration-300 ${activeTab === 'contacto' ? 'text-amber-500 bg-amber-500/5' : 'text-zinc-300 hover:text-white'}`}>CONTACTO</button>

        </div>

        <div className="mt-auto pb-32 flex flex-col items-center gap-4 opacity-50">
          <span className="text-[10px] text-zinc-500 font-bold tracking-widest uppercase">
            Armería AK-47 Guatemala
          </span>
          <div className="w-10 h-1 bg-amber-500/20 rounded-full"></div>
        </div>
      </div>

      {/* === SIDEBARS SOCIALES (Desktop & Mobile) === */}
      <div className="hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-40 flex-col gap-3">
        {/* ... (código social desktop sin cambios) ... */}
        <a
          href="https://wa.me/50230319972"
          target="_blank"
          className="flex items-center justify-end w-12 hover:w-44 h-12 bg-zinc-900/80 backdrop-blur border-l border-t border-b border-green-500/30 text-green-500 hover:text-white hover:bg-green-600 transition-all duration-300 rounded-l-xl group overflow-hidden shadow-lg"
        >
          <span className="whitespace-nowrap pr-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-4 group-hover:translate-x-0">
            WhatsApp
          </span>
          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
            <i className="ri-whatsapp-fill text-2xl"></i>
          </div>
        </a>
        <a
          href="https://www.instagram.com/armeriaak47"
          target="_blank"
          className="flex items-center justify-end w-12 hover:w-44 h-12 bg-zinc-900/80 backdrop-blur border-l border-t border-b border-pink-500/30 text-pink-500 hover:text-white hover:bg-pink-600 transition-all duration-300 rounded-l-xl group overflow-hidden shadow-lg"
        >
          <span className="whitespace-nowrap pr-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-4 group-hover:translate-x-0">
            Instagram
          </span>
          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
            <i className="ri-instagram-fill text-2xl"></i>
          </div>
        </a>
        <a
          href="mailto:armeriaak47sa@gmail.com"
          className="flex items-center justify-end w-12 hover:w-44 h-12 bg-zinc-900/80 backdrop-blur border-l border-t border-b border-amber-500/30 text-amber-500 hover:text-white hover:bg-amber-600 transition-all duration-300 rounded-l-xl group overflow-hidden shadow-lg"
        >
          <span className="whitespace-nowrap pr-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-4 group-hover:translate-x-0">
            Correo
          </span>
          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
            <i className="ri-mail-fill text-2xl"></i>
          </div>
        </a>
      </div>

      <div className="md:hidden fixed bottom-0 left-0 w-full bg-black/90 backdrop-blur-xl border-t border-zinc-800 z-[70] flex justify-around items-center h-20 pb-4 safe-area-pb shadow-[0_-5px_20px_rgba(0,0,0,0.5)]">
        <a
          href="https://wa.me/50230319972"
          target="_blank"
          className="flex flex-col items-center gap-1 text-zinc-500 hover:text-green-500 active:text-green-500 transition-colors w-1/3"
        >
          <i className="ri-whatsapp-fill text-2xl"></i>
          <span className="text-[10px] uppercase font-black tracking-wider">
            Chat
          </span>
        </a>

        <div className="relative -top-6 w-1/3 flex justify-center">
          <button
            onClick={() => setCartOpen(true)}
            className="bg-amber-500 text-black w-16 h-16 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(245,158,11,0.4)] border-[6px] border-black transition-transform active:scale-95"
          >
            <i className="ri-shopping-cart-2-fill text-2xl"></i>
            {cart.length > 0 && (
              <span className="absolute top-0 right-0 bg-red-600 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center border-2 border-black font-bold animate-bounce">
                {cart.length}
              </span>
            )}
          </button>
        </div>

        <a
          href="https://www.instagram.com/armeriaak47"
          target="_blank"
          className="flex flex-col items-center gap-1 text-zinc-500 hover:text-pink-500 active:text-pink-500 transition-colors w-1/3"
        >
          <i className="ri-instagram-fill text-2xl"></i>
          <span className="text-[10px] uppercase font-black tracking-wider">
            Social
          </span>
        </a>
      </div>

      {/* ============== CONTENIDO ============== */}

      {/* 1. INICIO */}
      {activeTab === "inicio" && (
        <section className="relative min-h-screen flex items-center justify-center pt-20">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>
          </div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <div className="inline-block mb-4 px-4 py-1 border border-amber-500/30 rounded-full bg-black/50 backdrop-blur-md">
              <span className="text-amber-500 font-black tracking-[0.2em] md:tracking-[0.5em] uppercase text-[10px] md:text-xs block animate-pulse">
                Armería Certificada Guatemala
              </span>
            </div>

            <h2 className="text-4xl sm:text-6xl md:text-8xl lg:text-9xl font-black uppercase italic leading-[1.1] mb-8 drop-shadow-2xl">
              <span className="text-white">La defensa es un</span> <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-b from-amber-400 to-amber-600 px-2">
                Derecho
              </span>
            </h2>

            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full max-w-md mx-auto sm:max-w-none">
              <button onClick={() => { if(categories.length > 0) handleNavClick('cat_' + categories[0].id) }} className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-4 rounded-xl font-black uppercase tracking-widest transition-all shadow-xl shadow-amber-500/20 active:scale-95 w-full sm:w-auto">Explorar Arsenal</button>
              <button onClick={() => handleNavClick('nosotros')} className="bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 text-white px-8 py-4 rounded-xl font-black uppercase tracking-widest transition-all active:scale-95 w-full sm:w-auto">Conócenos</button>
            </div>
          </div>
        </section>
      )}

      {/* 2. CATÁLOGO MEJORADO CON SUBCATEGORÍAS COMO BOTONES */}
      {activeTab.startsWith('cat_') && (() => {
        const catId = Number(activeTab.replace('cat_', ''));
        const parentCat = categories.find((c: any) => c.id === catId);
        
        return (
          <section className="max-w-7xl mx-auto px-4 md:px-6 pt-24 md:pt-36">
            
            {/* Cabecera y Buscador */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-8 md:mb-12">
              <div className="border-l-4 border-amber-500 pl-6 bg-black/40 backdrop-blur-sm py-4 rounded-r-xl inline-block pr-6">
                <h2 className="text-3xl md:text-5xl font-black italic uppercase leading-none break-words">
                  Catálogo <span className="text-zinc-400">/</span> <span className="text-amber-500 block md:inline">{parentCat?.name || 'Selección'}</span>
                </h2>
                <p className="text-zinc-300 mt-2 font-bold uppercase tracking-widest text-[10px] md:text-xs">
                  Equipamiento Táctico Profesional
                </p>
              </div>

              {/* Buscador Estético */}
              <div className="w-full md:w-96 relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i className="ri-search-line text-zinc-400 group-focus-within:text-amber-500 transition-colors"></i>
                </div>
                <input 
                  type="text" 
                  placeholder={`Buscar en ${parentCat?.name || 'productos'}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-black/60 border border-zinc-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-white text-sm rounded-2xl pl-12 pr-4 py-4 outline-none transition-all shadow-xl backdrop-blur-md"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-500 hover:text-amber-500 transition-colors">
                    <i className="ri-close-circle-fill text-lg"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Fila de Botones Píldoras para Subcategorías (Si el padre tiene hijos) */}
            {parentCat?.subcategories && parentCat.subcategories.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-12">
                <button 
                  onClick={() => setSelectedSubCategory('all')}
                  className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300 border shadow-lg ${
                    selectedSubCategory === 'all' 
                      ? 'bg-amber-500 text-black border-amber-500 shadow-amber-500/20 scale-105' 
                      : 'bg-black/50 text-zinc-300 border-zinc-700 hover:border-amber-500/50 hover:bg-zinc-900 backdrop-blur-md'
                  }`}
                >
                  <i className="ri-list-check-2 mr-2"></i> Todos
                </button>
                
                {parentCat.subcategories.map((sub: any) => (
                  <button 
                    key={sub.id}
                    onClick={() => setSelectedSubCategory(sub.id)}
                    className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300 border shadow-lg flex items-center gap-2 ${
                      selectedSubCategory === sub.id 
                        ? 'bg-zinc-100 text-black border-white shadow-white/20 scale-105' 
                        : 'bg-black/50 text-zinc-400 border-zinc-700 hover:border-zinc-500 hover:text-white hover:bg-zinc-900 backdrop-blur-md'
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            )}

            {/* Listado de Productos Filtrado */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.filter((p: any) => {
                // Filtro 1: Debe pertenecer al Padre actual
                const isFromParent = Number(p.category_id) === catId || Number(p.parent_id) === catId;
                if (!isFromParent) return false;

                // Filtro 2: Debe coincidir con la Subcategoría seleccionada
                const matchesSubCat = selectedSubCategory === 'all' ? true : Number(p.category_id) === selectedSubCategory;
                if (!matchesSubCat) return false;

                // Filtro 3: Debe coincidir con la Búsqueda
                const searchLower = searchQuery.toLowerCase();
                const matchesSearch = !searchLower || 
                                      p.name.toLowerCase().includes(searchLower) || 
                                      p.brand.toLowerCase().includes(searchLower) ||
                                      (p.caliber && p.caliber.toLowerCase().includes(searchLower));
                
                return matchesSearch;

              }).map(renderProductCard)}
            </div>
            
            {/* Mensaje si no hay resultados */}
            {products.filter((p: any) => {
                const isFromParent = Number(p.category_id) === catId || Number(p.parent_id) === catId;
                const matchesSubCat = selectedSubCategory === 'all' ? true : Number(p.category_id) === selectedSubCategory;
                const searchLower = searchQuery.toLowerCase();
                const matchesSearch = !searchLower || p.name.toLowerCase().includes(searchLower) || p.brand.toLowerCase().includes(searchLower) || (p.caliber && p.caliber.toLowerCase().includes(searchLower));
                return isFromParent && matchesSubCat && matchesSearch;
            }).length === 0 && (
              <div className="w-full py-20 flex flex-col items-center justify-center bg-black/40 rounded-3xl border border-zinc-800/50 mt-8 backdrop-blur-sm">
                <i className="ri-search-eye-line text-6xl text-zinc-700 mb-4"></i>
                <h3 className="text-xl font-black uppercase text-zinc-400">Sin Resultados</h3>
                <p className="text-zinc-500">No se encontraron productos en esta categoría o búsqueda.</p>
                <button onClick={() => {setSearchQuery(''); setSelectedSubCategory('all')}} className="mt-6 text-amber-500 text-sm font-bold uppercase underline hover:text-amber-400">Limpíar Filtros</button>
              </div>
            )}

          </section>
        )
      })()}

      {/* 3. TRÁMITES (AQUÍ ESTÁ EL CAMBIO IMPORTANTE) */}
      {activeTab === "tramites" && (
        <section className="max-w-4xl mx-auto px-4 md:px-6 pt-28 md:pt-36 flex flex-col items-center">
          <div className="w-full bg-black/60 backdrop-blur-xl p-6 md:p-12 rounded-3xl border border-amber-500/10 shadow-2xl">
            <h2 className="text-3xl md:text-5xl font-black italic uppercase mb-2">
              Trámites{" "}
              <span className="text-amber-500 underline decoration-amber-500/30">
                DIGECAM
              </span>
            </h2>
            <p className="text-zinc-400 mb-8 md:mb-10 text-xs font-black tracking-widest">
              ASESORÍA LEGAL PROFESIONAL
            </p>

            <form
              onSubmit={handleFormSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-amber-500 ml-1">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  className="w-full bg-black/50 p-4 rounded-xl border border-zinc-700 outline-none focus:border-amber-500 text-white transition-all focus:bg-black/80"
                  placeholder="Como aparece en DPI"
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-amber-500 ml-1">
                  Número de DPI
                </label>
                <input
                  type="text"
                  className="w-full bg-black/50 p-4 rounded-xl border border-zinc-700 outline-none focus:border-amber-500 text-white transition-all focus:bg-black/80"
                  placeholder="13 dígitos"
                  onChange={(e) =>
                    setFormData({ ...formData, dpi: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-amber-500 ml-1">
                  WhatsApp
                </label>
                <input
                  type="tel"
                  className="w-full bg-black/50 p-4 rounded-xl border border-zinc-700 outline-none focus:border-amber-500 text-white transition-all focus:bg-black/80"
                  placeholder="Para contactarte"
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-amber-500 ml-1">
                  Correo Electrónico
                </label>
                <input
                  type="email"
                  className="w-full bg-black/50 p-4 rounded-xl border border-zinc-700 outline-none focus:border-amber-500 text-white transition-all focus:bg-black/80"
                  placeholder="ejemplo@correo.com"
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-[10px] font-black uppercase text-amber-500 ml-1">
                  Tipo de Trámite
                </label>
                <div className="relative">
                  <select
                    className="w-full bg-black/50 p-4 rounded-xl border border-zinc-700 outline-none focus:border-amber-500 text-white transition-all appearance-none focus:bg-black/80"
                    onChange={(e) =>
                      setFormData({ ...formData, type_license: e.target.value })
                    }
                  >
                    <option value="primera_licencia">
                      Primera Licencia de Portación
                    </option>
                    <option value="renovacion">Renovación de Licencia</option>
                    <option value="traslado">Traslado de Dominio</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none">
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                </div>

                {/* Botón dinámico para abrir el formulario correspondiente de DIGECAM en otra pestaña */}
                <div className="flex justify-end pt-2">
                  <a
                    href={
                      formData.type_license === "primera_licencia"
                        ? "https://www.digecam.mil.gt/tramites-formulario/TVE9PQ=="
                        : formData.type_license === "renovacion"
                          ? "https://www.digecam.mil.gt/tramites-formulario/TVRBPQ=="
                          : "https://www.digecam.mil.gt/tramites-formulario/Tnc9PQ=="
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-4 py-2.5 rounded-xl border border-zinc-700 text-xs font-bold text-amber-500 hover:text-white transition-all shadow-lg active:scale-95 group"
                  >
                    <i className="ri-file-list-3-line"></i>
                    <span>Llenar Formulario Oficial DIGECAM</span>
                    <i className="ri-external-link-line opacity-50 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"></i>
                  </a>
                </div>
              </div>

              {/* === NUEVA SECCIÓN DE BOTONES === */}
              <div className="md:col-span-2 flex flex-col md:flex-row gap-4 mt-6">
                {/* Botón 1: Acción Principal (Enviar Formulario) */}
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-black py-5 rounded-xl uppercase tracking-[0.2em] shadow-xl shadow-amber-500/20 transition-all transform active:scale-95 flex items-center justify-center gap-2"
                >
                  <i className="ri-send-plane-fill"></i> Iniciar Gestión
                </button>

                {/* Botón 2: Acción Secundaria (Link Externo DIGECAM) */}
                <a
                  href="https://www.digecam.mil.gt/tramites-formulario/Tnc9PQ=="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-transparent border border-zinc-500 hover:border-white text-zinc-300 hover:text-white font-bold py-5 rounded-xl uppercase tracking-[0.1em] transition-all transform active:scale-95 flex items-center justify-center gap-2 group"
                >
                  <span>Requisitos</span>
                  <i className="ri-external-link-line group-hover:rotate-45 transition-transform"></i>
                </a>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* 4. NOSOTROS (VISIÓN) */}
      {activeTab === "nosotros" && (
        <section className="relative min-h-screen flex items-center justify-center py-24 md:py-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/80"></div>

          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center space-y-8">
            <div className="inline-block px-4 py-1 rounded-full border border-amber-500/30 bg-black/60 mb-4">
              <span className="text-amber-500 text-[10px] md:text-xs font-black tracking-[0.3em] uppercase">
                Nuestra Misión
              </span>
            </div>
            <h2 className="text-4xl md:text-7xl font-black italic uppercase text-white leading-none drop-shadow-2xl">
              Excelencia{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                Táctica
              </span>
            </h2>
            <div className="bg-black/50 backdrop-blur-sm p-6 rounded-2xl border border-white/5">
              <p className="text-base md:text-2xl text-zinc-200 font-light italic leading-relaxed max-w-4xl mx-auto text-justify md:text-center">
                "Ser la armería líder en la región, reconocida por la excelencia
                en la venta de armas, municiones y accesorios, distinguiéndonos
                por nuestro compromiso con la responsabilidad, la seguridad y la
                atención personalizada. Aspiramos a consolidarnos como la
                primera opción de nuestros clientes, ofreciendo soluciones que
                respondan a sus necesidades con profesionalismo, confianza y
                pasión."
              </p>
            </div>
          </div>
        </section>
      )}

      {/* 5. CONTACTO */}
      {activeTab === "contacto" && (
        <section className="min-h-screen pt-28 md:pt-36 pb-12 flex flex-col justify-center bg-black/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              <div className="space-y-8 order-2 lg:order-1">
                <h3 className="text-3xl md:text-4xl font-black italic uppercase text-white mb-8 border-l-8 border-amber-500 pl-6 drop-shadow-lg">
                  Contacto Directo
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {/* ... botones contacto ... */}
                  <a
                    href="https://wa.me/50230319972"
                    target="_blank"
                    className="flex items-center gap-6 bg-black/60 backdrop-blur-md p-6 rounded-2xl border border-zinc-800 hover:border-green-500/50 hover:bg-green-900/20 transition-all group active:scale-95"
                  >
                    <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="ri-whatsapp-fill text-3xl text-green-500"></i>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs font-black uppercase tracking-widest">
                        WhatsApp
                      </p>
                      <p className="text-white text-lg font-bold">
                        +502 3031 9972
                      </p>
                    </div>
                  </a>
                  {/* ... resto de botones ... */}
                  <a
                    href="https://www.instagram.com/armeriaak47"
                    target="_blank"
                    className="flex items-center gap-6 bg-black/60 backdrop-blur-md p-6 rounded-2xl border border-zinc-800 hover:border-pink-500/50 hover:bg-pink-900/20 transition-all group active:scale-95"
                  >
                    <div className="w-12 h-12 bg-pink-500/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="ri-instagram-fill text-3xl text-pink-500"></i>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs font-black uppercase tracking-widest">
                        Instagram
                      </p>
                      <p className="text-white text-lg font-bold">
                        @armeriaak47
                      </p>
                    </div>
                  </a>

                  <a
                    href="mailto:armeriaak47sa@gmail.com"
                    className="flex items-center gap-6 bg-black/60 backdrop-blur-md p-6 rounded-2xl border border-zinc-800 hover:border-amber-500/50 hover:bg-amber-900/20 transition-all group active:scale-95"
                  >
                    <div className="w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="ri-mail-send-fill text-3xl text-amber-500"></i>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs font-black uppercase tracking-widest">
                        Correo Electrónico
                      </p>
                      <p className="text-white text-lg font-bold break-all">
                        armeriaak47sa@gmail.com
                      </p>
                    </div>
                  </a>
                </div>
              </div>

              <div className="relative h-64 md:h-[600px] rounded-3xl overflow-hidden border border-zinc-800 shadow-2xl bg-zinc-900 order-1 lg:order-2">
                <iframe
                  src="https://maps.google.com/maps?q=14.5029092,-89.649263&t=&z=17&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{
                    border: 0,
                    filter: "invert(90%) hue-rotate(180deg)",
                  }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación Armería AK-47"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* === MODAL DE DETALLE DE PRODUCTO === */}
      {selectedProduct && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/90 backdrop-blur-md transition-opacity"
            onClick={() => setSelectedProduct(null)}
          ></div>

          <div className="relative w-full max-w-5xl h-[85vh] bg-zinc-900/90 border border-amber-500/30 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(245,158,11,0.2)] flex flex-col animate-fade-in-up">
            <button
              onClick={() => setSelectedProduct(null)}
              className="absolute top-4 right-4 z-50 text-white bg-black/50 p-2 rounded-full hover:bg-amber-500 hover:text-black transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {/* Controles de Vista de la Tarjeta */}
            <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col">
              {/* Parte Superior: Información */}
              <div className="w-full p-6 md:p-8 flex flex-col bg-gradient-to-br from-zinc-900 via-zinc-900 to-black shrink-0 border-b border-zinc-800">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                  <div>
                    <p className="text-amber-500 font-bold uppercase tracking-[0.2em] text-xs mb-2 flex items-center gap-2">
                      <span className="w-8 h-[2px] bg-amber-500 inline-block"></span>
                      {selectedProduct.brand || "Armería AK47"}
                    </p>
                    <h2 className="text-base md:text-lg font-black italic uppercase text-white leading-[0.9] pr-12">
                      {selectedProduct.name}
                    </h2>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl font-black text-white italic">
                      Q{Number(selectedProduct.price).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                    <p className="text-[10px] text-zinc-500 uppercase font-black">
                      Calibre
                    </p>
                    <p className="text-sm md:text-base text-white font-bold">
                      {selectedProduct.caliber || "N/A"}
                    </p>
                  </div>
                  <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                    <p className="text-[10px] text-zinc-500 uppercase font-black">
                      ID Referencia
                    </p>
                    <p className="text-sm md:text-base text-white font-bold">
                      #AK-{selectedProduct.id}
                    </p>
                  </div>
                  <div className="col-span-2 flex items-end">
                    <button
                      onClick={() => addToCart(selectedProduct)}
                      className="w-full h-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black text-sm md:text-base font-black uppercase py-2 px-4 rounded-xl shadow-[0_0_30px_rgba(245,158,11,0.3)] transition-all active:scale-95 flex items-center justify-center gap-2"
                    >
                      <i className="ri-shopping-cart-2-fill text-lg"></i> Añadir
                      al Arsenal
                    </button>
                  </div>
                </div>
              </div>

              {/* Parte Inferior: Imagen a pantalla completa sin recorte con zoom on hover */}
              <div className="w-full flex-1 min-h-[40vh] bg-black relative flex items-center justify-center overflow-hidden p-6 md:p-12 group cursor-zoom-in">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-900/20 to-black z-0 transition-opacity duration-500 group-hover:opacity-40"></div>
                <img
                  src={
                    selectedProduct.image_url ||
                    "https://via.placeholder.com/800x600?text=Sin+Imagen"
                  }
                  className="relative z-10 w-full max-h-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.5]"
                  alt={selectedProduct.name}
                  style={{ objectPosition: "center" }}
                />
                <div className="absolute bottom-4 left-4 z-20 flex gap-2 w-full justify-between pr-8">
                  <div className="px-3 py-1 bg-amber-500 text-black text-[10px] font-black uppercase tracking-widest rounded shadow-lg transition-all duration-300 group-hover:opacity-0 group-hover:translate-y-4">
                    {selectedProduct.category_name || "Táctico"}
                  </div>
                  <div className="px-3 py-1 bg-black/50 text-white/50 border border-white/10 text-[10px] uppercase tracking-widest rounded shadow-lg opacity-100 transition-opacity duration-300 group-hover:opacity-0 flex items-center gap-2">
                    <i className="ri-zoom-in-line"></i> Hover para Zoom
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CARRITO SIDEBAR */}
      {cartOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setCartOpen(false)}
          ></div>
          <div className="relative w-full max-w-md bg-zinc-950 h-full p-6 md:p-8 border-l border-amber-500/20 flex flex-col shadow-2xl">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-black italic bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
                TU CARRITO
              </h2>
              <button
                onClick={() => setCartOpen(false)}
                className="text-3xl text-amber-500 hover:rotate-90 transition-transform"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-2 custom-scrollbar">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center opacity-30 text-center">
                  <i className="ri-shopping-cart-line text-6xl mb-4"></i>
                  <p>Tu arsenal está vacío</p>
                </div>
              ) : (
                cart.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 bg-white/5 p-4 rounded-2xl border border-zinc-800 group hover:border-amber-500/30 transition-all"
                  >
                    <img
                      src={item.image_url}
                      className="w-16 h-16 object-cover rounded-xl"
                      alt={item.name}
                    />
                    <div className="flex-1">
                      <h4 className="font-black uppercase text-xs text-white leading-tight mb-1">
                        {item.name}
                      </h4>
                      <p className="text-amber-500 font-black text-sm">
                        Q{item.price.toLocaleString()}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-zinc-500 font-bold uppercase">
                          Cant: {item.quantity}
                        </span>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-red-500 text-[10px] font-black uppercase hover:underline"
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-zinc-800">
              <div className="flex justify-between items-center mb-6">
                <span className="text-xs font-black uppercase text-zinc-500 tracking-widest">
                  Total Estimado
                </span>
                <span className="text-3xl font-black italic text-white">
                  Q{getTotalPrice().toLocaleString()}
                </span>
              </div>
              <button
                onClick={sendWhatsAppMessage}
                className="w-full bg-green-600 hover:bg-green-500 text-white font-black py-4 rounded-xl uppercase tracking-widest shadow-2xl shadow-green-600/20 flex items-center justify-center gap-3 transition-all active:scale-95"
              >
                <i className="ri-whatsapp-line text-2xl"></i> Consultar WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
